<template>
  <div
    id="app"
    :class="{
      'ai-assistant-open': showAiAssistant,
      'ai-assistant-fullscreen': showAiAssistant && chatSize === 'full',
    }"
  >
    <b-navbar
      :toggleable="updateExists ? 'lg' : 'md'"
      type="dark"
      :variant="navbarVariant"
      class="gid-header"
      :class="{ 'flex-wrap': !updateExists }"
    >
      <back-button class="mr-2" />
      <router-link
        to="/"
        class="d-flex flex-wrap align-items-center justify-content-center"
      >
        <env-orbit-logo :environment="environment" :radius="9" :duration="16">
          <b-navbar-brand to="/" class="mr-auto">
            <img
              v-if="brandLogo"
              :src="brandLogo"
              alt="GID BRAND PORTAL"
              class="gid-logo_img"
            />
            <template v-else>GID BRAND PORTAL</template>
          </b-navbar-brand>
        </env-orbit-logo>
      </router-link>
      <b-navbar-toggle
        target="nav_collapse"
        v-if="!updateExists"
      ></b-navbar-toggle>
      <b-collapse v-if="isAuthenticated" is-nav id="nav_collapse">
        <b-navbar-nav>
          <template v-for="(item, index) in navItemsFiltered">
            <b-nav-item
              :key="index"
              :to="{ name: item }"
              :class="{ active: isQuoteApprovalPage(item) }"
            >
              {{ $t(`nav.${item}`) }}
              <img
                v-if="showNotifyProposalsIcon(item)"
                src="@/assets/icons/notify_icon.svg"
                alt="approval-jobs"
                class="warning-icon"
              />
            </b-nav-item>
          </template>
        </b-navbar-nav>
        <b-navbar-nav class="ml-auto">
          <b-nav-item
            @click="toggleAiAssistant"
            v-if="hasAiAssistantAccess"
            href="#"
            class="ai-assistant-toggle"
            v-b-tooltip.hover.bottom
            title="Open AI Assistant (Press Ctrl+/ or Ctrl+ß)"
          >
            <b-icon-robot class="mr-1"></b-icon-robot>
            AI Assistant
            <small class="ml-1 text-muted">(Ctrl+/)</small>
          </b-nav-item>
          <b-nav-item-dropdown is-nav id="nav_collapse" right>
            <template #button-content>
              <i class="material-icons align-middle" v-e2e:profileDropdown>
                person
              </i>
            </template>
            <b-nav-item v-if="showSettings" :to="{ name: 'settings' }">
              <span class="profile-menu-option">{{ $t(`nav.settings`) }}</span>
            </b-nav-item>
            <b-nav-item @click="logout">
              <span class="profile-menu-option" v-e2e:logoutLink>
                {{ $t('nav.logout') }}
              </span>
            </b-nav-item>
          </b-nav-item-dropdown>
        </b-navbar-nav>
      </b-collapse>
      <b-collapse v-else is-nav id="nav_collapse">
        <b-navbar-nav class="ml-auto">
          <b-nav-item v-e2e:loginLink to="/login">
            {{ $t('nav.login') }}
          </b-nav-item>
        </b-navbar-nav>
      </b-collapse>
      <refresh-button v-if="updateExists" :onRefresh="refreshApp" />
    </b-navbar>

    <!-- Wrapped in .brands-app-content so the AI assistant fullscreen mode
         can hide the whole content area with a single CSS rule while
         keeping the top navbar (.gid-header) and the chat itself visible. -->
    <div class="brands-app-content">
      <b-container fluid>
        <b-alert
          v-if="notFoundUrl"
          variant="warning"
          dismissible
          show
          class="mt-3"
          @dismissed="clearNotFound"
        >
          {{ $t('not-found-redirect', { url: notFoundUrl }) }}
        </b-alert>
      </b-container>
      <bootstrap-loader v-if="hasAccess === null" />
      <router-view v-else-if="hasAccess" />
      <div v-else class="text-center">
        <h4 class="m-5">{{ $t('no-access.title') }}</h4>
        <p v-if="isAuthenticated">{{ $t('no-access.description') }}</p>
        <i18n v-else path="no-access.login" tag="p" class="mb-0">
          <router-link slot="login" to="/login">
            {{ $t('no-access.login-link') }}
          </router-link>
        </i18n>
      </div>
    </div>

    <add-to-home-screen app-name="GID Brand" />
    <offline />

    <llama-chat
      v-if="showAiAssistant"
      @close="closeAiAssistant"
      @size-change="chatSize = $event"
      position="right"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { PURGE_AUTH } from '@gid/vue-common/store/auth.module/types';
import { checkRouteAccess } from '@gid/vue-common/store/auth.module';
import update from '@gid/vue-common/components/mixins/update';
import { getEnv } from '@gid/vue-common/utils';
import {
  AccountProfileEnum,
  ContactProfileEnum,
  UserRoleEnum,
} from '@gid/models';
import axios from 'axios';
import { SET_PENDING_PROPOSAL_MENU_ITEM } from '@/store/modules/mainModule/types';
import LlamaChat from '@gid/vue-common/components/chat-view/llama-chat.vue';
import EnvOrbitLogo from '@gid/vue-common/components/EnvOrbitLogo.vue';
import BootstrapLoader from '@gid/vue-common/components/BootstrapLoader.vue';
import { BIconRobot } from 'bootstrap-vue';

export default {
  mixins: [update],
  components: {
    LlamaChat,
    BIconRobot,
    EnvOrbitLogo,
    BootstrapLoader,
  },
  data() {
    return {
      showAiAssistant: false,
      chatSize: 'default',
    };
  },
  computed: {
    ...mapGetters([
      'isAuthenticated',
      'contactProfiles',
      'user',
      'environment',
      'access_token',
      'hasPendingProposals',
      'shouldShowProposalMenuItems',
    ]),
    navbarVariant() {
      if (this.user?.profiles?.includes('readonly')) {
        return 'readonly';
      } else if (this.environment === 'production') {
        return 'primary';
      } else {
        return 'staging';
      }
    },
    navItems() {
      const routes = [
        'order-intake-forms-list',
        'job-create',
        'upload',
        'jobs',
        'projects',
        'invoices',
        // 'dashboard', - Temporary disable as per: https://gidt.atlassian.net/browse/GID-300
      ];
      if (this.isProjectsRelated) {
        if (this.shouldShowProposalMenuItems) {
          routes.splice(2, 0, 'quote-approval-jobs');
        }
      } else {
        routes.splice(2, 0, 'jobs-affiliate');
      }
      return routes;
    },
    brandLogo() {
      return (
        this.user &&
        this.user.account &&
        this.user.account.id &&
        `https://${getEnv('S3_HOST')}/brand/${
          this.user.account.id
        }/upload/assets/forApps`
      );
    },
    hasAccess() {
      return checkRouteAccess({
        route: this.$route,
        user: this.user,
        isAuthenticated: this.isAuthenticated,
      });
    },
    isProjectsRelated() {
      if (
        this.user?.account?.profiles?.includes(AccountProfileEnum.PROJECT_VIEW)
      ) {
        const allowedContactProfiles = [
          ContactProfileEnum.ADMIN,
          ContactProfileEnum.REPORTING,
          ContactProfileEnum.PROJECT_MANAGER_GENERIC,
          ContactProfileEnum.PROJECT_MANAGER_DEFAULT,
        ];
        return (
          this.user.contact.profiles.some((p) =>
            allowedContactProfiles.includes(p),
          ) && this.user?.role === UserRoleEnum.BRAND
        );
      }
      return false;
    },
    navItemsFiltered() {
      return this.navItems.filter((item) => {
        const resolved = this.$router.resolve({ name: item });
        if (resolved) {
          return checkRouteAccess({
            route: resolved.route,
            user: this.user,
            isAuthenticated: this.isAuthenticated,
          });
        } else {
          return false;
        }
      });
    },
    isQuoteApprovalPage() {
      return (pageName) =>
        pageName === 'quote-approval-jobs' &&
        this.$route.name === 'quote-approval-jobs';
    },
    showNotifyProposalsIcon() {
      return (pageName) =>
        pageName === 'quote-approval-jobs' && this.hasPendingProposals;
    },
    showSettings() {
      return this.contactProfiles.some((cp) =>
        [
          ContactProfileEnum.PROJECT_MANAGER_DEFAULT,
          ContactProfileEnum.PROJECT_MANAGER_GENERIC,
          ContactProfileEnum.ADMIN,
        ].includes(cp),
      );
    },
    notFoundUrl() {
      return this.$route.query.notFound || null;
    },
    hasAiAssistantAccess() {
      const isChatEnabled =
        getEnv('VUE_APP_ENABLE_CHAT_BRANDS') === 'true' ||
        getEnv('VUE_APP_ENABLE_CHAT_BRANDS') === '1';
      if (!isChatEnabled) {
        return false;
      }
      return this.isAuthenticated && this.user;
    },
  },
  watch: {
    user: function (loggedUser) {
      if (loggedUser?.account?.id) {
        const brandId = loggedUser?.account?.id;
        axios
          .get(
            `/data-api/entity/opportunity-option/quote-approval-accessible/${brandId}`,
          )
          .then(({ data }) => {
            const { hasProposalJobs, showProposals } = data;
            this.$store.commit(
              SET_PENDING_PROPOSAL_MENU_ITEM,
              {
                hasPendingProposals: hasProposalJobs,
                showProposalsMenuItem: showProposals,
              },
              { root: true },
            );
          });
      }
    },
  },
  methods: {
    logout() {
      this.$store.commit(PURGE_AUTH);
    },
    clearNotFound() {
      if (this.$route.query.notFound) {
        const query = { ...this.$route.query };
        delete query.notFound;
        this.$router.replace({ query });
      }
    },
    toggleAiAssistant() {
      this.showAiAssistant = !this.showAiAssistant;
    },
    closeAiAssistant() {
      this.showAiAssistant = false;
    },
    handleKeyboardShortcuts(event) {
      if (!this.hasAiAssistantAccess) {
        return;
      }

      const isShortcutKey = event.key === '/' || event.key === 'ß';
      const isCtrlShortcut = (event.ctrlKey || event.metaKey) && isShortcutKey;

      if (!isShortcutKey) {
        return;
      }

      const activeElement = document.activeElement;
      const isInInputField =
        activeElement &&
        (activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA' ||
          activeElement.tagName === 'SELECT' ||
          activeElement.isContentEditable ||
          activeElement.closest('[contenteditable="true"]'));

      // Ctrl+/ or Ctrl+ß works everywhere, even in input fields
      if (isCtrlShortcut) {
        const isInAssistant =
          activeElement && activeElement.closest('.llama-chat-container');
        const isInAssistantTextarea =
          isInAssistant && activeElement.tagName === 'TEXTAREA';

        // Toggle assistant unless typing in the assistant's textarea
        if (!isInAssistantTextarea) {
          event.preventDefault();
          this.showAiAssistant = !this.showAiAssistant;
        }
        return;
      }

      // Regular / or ß (without Ctrl) only works when NOT in input fields
      if (!this.showAiAssistant && !isInInputField) {
        event.preventDefault();
        this.showAiAssistant = true;
        return;
      }

      if (this.showAiAssistant) {
        const isInAssistant =
          activeElement && activeElement.closest('.llama-chat-container');
        const isInAssistantTextarea =
          isInAssistant && activeElement.tagName === 'TEXTAREA';

        if (!isInAssistantTextarea) {
          event.preventDefault();
          this.showAiAssistant = false;
        }
      }
    },
  },
  mounted() {
    document.addEventListener('keydown', this.handleKeyboardShortcuts);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeyboardShortcuts);
  },
};
</script>

<style lang="scss">
@import '@gid/vue-common/scss/_variables.scss';
.profile-menu-option {
  color: #04a789;
}
.gid-logo_img {
  max-height: 40px;
  min-height: 40px;
  max-width: 200px;
}
.gid-header {
  &.navbar {
    position: sticky;
    top: 0;
    flex-wrap: nowrap;
    z-index: 1032;
    padding: 0.7rem 1rem;
    gap: 0.5rem 1rem;
  }
}

#app {
  transition: margin-right 0.3s ease;

  // Brands has no left sidebar (its main menu is the top .gid-header),
  // so the AI assistant's fullscreen mode should span the full viewport
  // width. The chat uses --sidebar-width-collapsed as its left inset,
  // defaulting to 59px (admin's collapsed sidebar) — override to 0 here.
  --sidebar-width-collapsed: 0px;

  &.ai-assistant-open {
    margin-right: 500px;

    @media (max-width: 1400px) {
      margin-right: 400px;
    }

    @media (max-width: 768px) {
      margin-right: 0;
    }
  }

  // Hide the main content area when the AI assistant is in fullscreen.
  // The sticky top navbar (.gid-header) stays visible because it lives
  // outside .brands-app-content. The class on #app is set reactively
  // from the chat's @size-change event, so any path that closes the
  // chat (button, keyboard shortcut, programmatic) automatically drops
  // the class and restores the content.
  &.ai-assistant-fullscreen {
    margin-right: 0; // chat is full-viewport, no need to push content
    .brands-app-content {
      display: none !important;
    }
  }
}
</style>

<style scoped>
.nav-item.active {
  border: 1px solid #e2e2e2;
  border-radius: 2px;
}
</style>
